import { toast } from "react-hot-toast";
import {
  Form,
  LoaderFunctionArgs,
  ActionFunctionArgs,
  useLoaderData,
  redirect,
} from "react-router-dom";
import IndeterminateCheckbox from "../../components/inputs/IndeterminantCheckbox";
import { Input, TextArea } from "../../components/inputs/Input";
import CustomSelect from "../../components/customSelect";
import { FormLayout } from "../../layout/FormLayout";
import { createApolloClient } from "../../providers/ApolloClientFactory";
import { GET_LIST_EVENTS_BY_ORGANIZATION } from "../../queries";
import { organizationStore } from "../../store/organization";
import { Attendee } from "../../types/attendee";
import { Event } from "../../types/event";
import { CREATE_ATTENDEE } from "./mutations";
import { GET_ATTENDEE } from "./queries";
import { userStore } from "../../store/user";

const client = createApolloClient();

const { getState } = organizationStore;
const { getState: getUserStore } = userStore;

export const attendeeCloneLoader = async ({ params }: LoaderFunctionArgs) => {
  const organizationId = getState().organizationId;
  const user = getUserStore().user;

  let where: any = {
    organization_id: { _eq: organizationId },
  };
  if (user?.events) {
    where = {
      ...where,
      id: {
        _in: user?.events,
      },
    };
  }

  const [{ data: attendeeData }, { data: eventsData }] = await Promise.all([
    client.query({
      query: GET_ATTENDEE,
      variables: {
        id: params?.id,
      },
    }),
    client.query({
      query: GET_LIST_EVENTS_BY_ORGANIZATION,
      variables: {
        where,
      },
    }),
  ]);

  return {
    attendee: attendeeData.attendees_by_pk,
    events: eventsData.events,
  };
};

export const attendeeCloneAction = async ({
  request,
}: ActionFunctionArgs) => {
  const body = await request.formData();

  const input = {
    email: body.get("email"),
    first_name: body.get("first_name"),
    last_name: body.get("last_name"),
    phone_number: body.get("phone_number"),
    event_id: parseInt(body.get("event_id") as string),
    is_active: body.get("is_active"),
    is_suspended: body.get("is_suspended"),
    note: body.get("note") || null,
  };

  try {
    const { data } = await client.mutate({
      mutation: CREATE_ATTENDEE,
      variables: {
        input,
      },
    });

    toast.success("Attendee cloned successfully");
    return redirect(`/attendees/list`);
  } catch (error: any) {
    const errorType = error?.graphQLErrors?.[0]?.extensions?.code;
    const message = error?.graphQLErrors?.[0]?.message;
    if (errorType === "constraint-violation") {
      toast.error(message);
    } else {
      toast.error("Failed to clone attendee: " + error);
    }
    return null;
  }
};

export const AttendeeClone = () => {
  const { attendee, events } = useLoaderData() as {
    attendee: Attendee;
    events: Event[];
  };

  return (
    <Form action={`/attendees/${attendee.id}/clone`} method="post">
      <FormLayout>
        <div className="col-span-6 sm:col-span-4">
          <Input
            label="Email"
            name="email"
            type="email"
            defaultValue={attendee?.email}
            required
          />
        </div>
        <div className="col-span-3 sm:col-span-1">
          <IndeterminateCheckbox
            name="is_suspended"
            label="Is Suspended"
            defaultChecked={attendee.is_suspended}
          />
        </div>
        <div className="col-span-3 sm:col-span-1">
          <IndeterminateCheckbox
            name="is_active"
            label="Is Active"
            defaultChecked={attendee.is_active}
          />
        </div>
        <div className="col-span-6">
          <Input
            label="First Name"
            name="first_name"
            defaultValue={attendee.first_name}
          />
        </div>
        <div className="col-span-6">
          <Input
            label="Last Name"
            name="last_name"
            defaultValue={attendee.last_name}
          />
        </div>
        <div className="col-span-6">
          <Input
            label="Phone Number"
            name="phone_number"
            defaultValue={attendee.phone_number}
          />
        </div>
        <div className="col-span-6">
          <CustomSelect
            title="Event"
            name="event_id"
            defaultValue={attendee.event_id}
            required
            options={[
              ...events.map((event) => ({
                label: event.name,
                value: event.id,
              })),
            ]}
          />
        </div>
        <div className="col-span-6 sm:col-span-6">
          <TextArea name="note" label="Note" defaultValue={attendee.note} />
        </div>
      </FormLayout>
    </Form>
  );
};
